<template>
  <div>
    <vue3-html2pdf
      :show-layout="false"
      :float-layout="true"
      :enable-download="false"
      :preview-modal="false"
      :paginate-elements-by-height="1120"
      filename="FomoTicket"
      :pdf-quality="1.7"
      :manual-pagination="false"
      pdf-format="a4"
      pdf-orientation="portrait"
      pdf-content-width="800px"
      @beforeDownload="beforeDownload($event)"
      ref="html2Pdf"
    >
      <template #pdf-content>
        <section class="pdf-ticket-sheet" id="ticket-container">
          <article
            v-for="(ticket, index) in normalizedTickets"
            :key="ticket.unique_key"
            class="pdf-ticket-card"
          >
            <div class="pdf-ticket-card__rail">
              <span>FOMO PASS</span>
              <strong>#{{ displayTicketNumber(index) }}</strong>
              <small>{{ formatDateCompact(event.start_date) }}</small>
            </div>

            <div class="pdf-ticket-card__body">
              <header class="pdf-ticket-card__header">
                <img
                  v-if="event.banner_url"
                  :src="event.banner_url"
                  alt="Event Banner"
                  class="pdf-ticket-card__banner"
                />

                <div class="pdf-ticket-card__hero-copy">
                  <span class="pdf-ticket-card__eyebrow">Ticket valido</span>
                  <h1>{{ event.name }}</h1>
                  <p>{{ venueLabel }}</p>
                </div>
              </header>

              <section class="pdf-ticket-card__meta-grid">
                <div class="pdf-ticket-card__meta-item">
                  <span>Localidad</span>
                  <strong>{{ ticket.name || 'Acceso general' }}</strong>
                </div>

                <div class="pdf-ticket-card__meta-item">
                  <span>Asistente</span>
                  <strong>{{ attendeeName(ticket) }}</strong>
                </div>

                <div class="pdf-ticket-card__meta-item">
                  <span>Fecha</span>
                  <strong>{{ formatDateLong(event.start_date) }}</strong>
                </div>

                <div class="pdf-ticket-card__meta-item">
                  <span>Asiento</span>
                  <strong>{{ ticket.seat_name || 'Acceso libre' }}</strong>
                </div>
              </section>

              <footer class="pdf-ticket-card__footer">
                <div>
                  <span class="pdf-ticket-card__footer-label">Codigo de acceso</span>
                  <strong>{{ displayTicketCode(ticket, index) }}</strong>
                </div>

                <div>
                  <span class="pdf-ticket-card__footer-label">Orden</span>
                  <strong>#{{ invoice }}</strong>
                </div>
              </footer>
            </div>

            <aside class="pdf-ticket-card__qr-panel">
              <div class="pdf-ticket-card__qr-shell">
                <qr-code :size="150" :text="ticketQrValue(ticket, index)" class="pdf-ticket-card__qr"></qr-code>
              </div>

              <div class="pdf-ticket-card__qr-copy">
                <span>Scan ready</span>
                <strong>{{ ticket.name || 'Ticket' }}</strong>
                <small>Presenta este QR en ingreso o conserva el archivo para validacion manual.</small>
              </div>
            </aside>
          </article>
        </section>
      </template>
    </vue3-html2pdf>
  </div>
</template>

<script>
import Vue3Html2pdf from 'vue3-html2pdf'

export default {
  name: 'PdfGeneration',

  props: {
    ticketsTable: {
      type: Array,
      required: true,
    },
    event: {
      type: Object,
      required: true,
    },
    invoice: {
      type: Number,
      required: true,
    },
  },

  components: {
    Vue3Html2pdf,
  },

  computed: {
    normalizedTickets() {
      return (Array.isArray(this.ticketsTable) ? this.ticketsTable : []).map((ticket, index) => ({
        ...ticket,
        unique_key: `${this.invoice}-${ticket.id || index}-${ticket.access_code_snapshot || 'ticket'}`,
      }))
    },

    venueLabel() {
      const venueName = this.event && this.event.venue ? this.event.venue.name : ''
      return venueName || 'Venue por confirmar'
    },
  },

  mounted() {
    this.generateReport()
  },

  methods: {
    generateReport() {
      this.$refs.html2Pdf.generatePdf()
    },

    async beforeDownload({ html2pdf, options, pdfContent }) {
      const worker = await html2pdf()
        .set(options)
        .from(pdfContent)
        .toPdf()
        .output('blob')

      const file = new File([worker], 'ticket.pdf')
      this.$emit('messageFromPdfGeneration', file)
    },

    attendeeName(ticket) {
      const name = `${ticket.first_name || ''} ${ticket.last_name || ''}`.trim()
      return name || 'Sin nombre asignado'
    },

    ticketCode(ticket, index) {
      return ticket.access_code_snapshot || `FOMO-${this.invoice}-${index + 1}`
    },

    ticketQrValue(ticket, index) {
      return `FOMO|TICKET|${this.ticketCode(ticket, index)}`
    },

    displayTicketCode(ticket, index) {
      const raw = this.ticketCode(ticket, index).replace(/[^A-Z0-9]/gi, '').toUpperCase()
      const compact = raw.slice(-12).padStart(12, '0')
      return `FOMO-${compact.slice(0, 4)}-${compact.slice(4, 8)}-${compact.slice(8, 12)}`
    },

    displayTicketNumber(index) {
      return `${this.invoice}-${index + 1}`
    },

    formatDateCompact(value) {
      if (!value) {
        return 'Fecha por confirmar'
      }

      return new Intl.DateTimeFormat('default', {
        dateStyle: 'medium',
      }).format(new Date(value))
    },

    formatDateLong(value) {
      if (!value) {
        return 'Fecha por confirmar'
      }

      return new Intl.DateTimeFormat('default', {
        dateStyle: 'full',
        timeStyle: 'short',
      }).format(new Date(value))
    },
  },
}
</script>

<style scoped>
.pdf-ticket-sheet {
  margin: 0;
  padding: 18px;
  background:
    radial-gradient(circle at top left, rgba(117, 225, 238, 0.08), transparent 24%),
    radial-gradient(circle at top right, rgba(126, 104, 194, 0.1), transparent 22%),
    #09090f;
}

.pdf-ticket-card {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr) 220px;
  gap: 0;
  margin: 0 0 18px;
  border-radius: 28px;
  overflow: hidden;
  background:
    radial-gradient(circle at top right, rgba(109, 82, 170, 0.16), transparent 22%),
    radial-gradient(circle at bottom left, rgba(107, 214, 229, 0.08), transparent 22%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.01)),
    #0f1118;
  border: 1px solid rgba(201, 211, 231, 0.12);
  box-shadow: 0 24px 54px rgba(0, 0, 0, 0.24);
  page-break-inside: avoid;
  break-inside: avoid;
}

.pdf-ticket-card__rail {
  display: grid;
  align-content: space-between;
  gap: 14px;
  padding: 18px 14px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.01)),
    linear-gradient(180deg, rgba(29, 34, 48, 0.95), rgba(13, 14, 20, 0.98));
  border-right: 1px dashed rgba(201, 211, 231, 0.12);
}

.pdf-ticket-card__rail span,
.pdf-ticket-card__footer-label,
.pdf-ticket-card__meta-item span,
.pdf-ticket-card__eyebrow,
.pdf-ticket-card__qr-copy span {
  color: #bfc8db;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.pdf-ticket-card__rail strong,
.pdf-ticket-card__footer strong,
.pdf-ticket-card__meta-item strong,
.pdf-ticket-card__hero-copy h1,
.pdf-ticket-card__qr-copy strong {
  color: #ffffff;
}

.pdf-ticket-card__body {
  display: grid;
  gap: 18px;
  padding: 20px 22px;
}

.pdf-ticket-card__header {
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr);
  gap: 18px;
  align-items: center;
}

.pdf-ticket-card__banner {
  width: 92px;
  height: 92px;
  border-radius: 18px;
  object-fit: cover;
  display: block;
}

.pdf-ticket-card__hero-copy {
  display: grid;
  gap: 8px;
}

.pdf-ticket-card__hero-copy h1 {
  margin: 0;
  font-size: 1.82rem;
  line-height: 0.95;
  font-family: 'Syne', 'Bodoni Moda', serif;
}

.pdf-ticket-card__hero-copy p,
.pdf-ticket-card__qr-copy small {
  margin: 0;
  color: rgba(194, 204, 223, 0.84);
  line-height: 1.55;
}

.pdf-ticket-card__meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.pdf-ticket-card__meta-item {
  display: grid;
  gap: 6px;
  padding: 12px 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(201, 211, 231, 0.08);
}

.pdf-ticket-card__footer {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: end;
  padding-top: 8px;
  border-top: 1px solid rgba(201, 211, 231, 0.08);
}

.pdf-ticket-card__footer div {
  display: grid;
  gap: 6px;
}

.pdf-ticket-card__qr-panel {
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 14px;
  padding: 20px 18px;
  background:
    radial-gradient(circle at top, rgba(214, 221, 232, 0.08), transparent 24%),
    linear-gradient(180deg, rgba(18, 21, 30, 0.96), rgba(9, 10, 15, 0.98));
  border-left: 1px solid rgba(201, 211, 231, 0.08);
}

.pdf-ticket-card__qr-shell {
  padding: 14px;
  border-radius: 28px;
  background: #ffffff;
  box-shadow: 0 20px 36px rgba(0, 0, 0, 0.18);
}

.pdf-ticket-card__qr {
  display: block;
}

.pdf-ticket-card__qr-copy {
  display: grid;
  gap: 6px;
  justify-items: center;
  text-align: center;
}
</style>
